import React, { useEffect } from 'react';
import { Badge, Button, Card, Carousel, Col, ProgressBar, Row } from 'react-bootstrap';
import { Link } from '@tanstack/react-location';
import { FaBuilding, FaChevronLeft, FaChevronRight, FaUser } from 'react-icons/fa';
import { PlateClassification } from '../../Types';
import {
  RegistrationFileSource,
  RegistrationReviewItem
} from '../../types/registrationReview.types';
import RegistrationPreview from './RegistrationPreview';

const formatDate = (value: string | null) =>
  value
    ? new Date(value).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    : '';

const classificationLabel = (classification: PlateClassification) => {
  switch (classification) {
    case PlateClassification.Business:
      return { label: 'Business', bg: 'success' };
    case PlateClassification.Personal:
      return { label: 'Personal', bg: 'info' };
    default:
      return { label: 'Unassigned', bg: 'secondary' };
  }
};

const isTypingTarget = (target: EventTarget | null) => {
  const element = target as HTMLElement | null;
  if (!element) return false;
  const tag = element.tagName;
  return tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA' || element.isContentEditable;
};

type Props = {
  items: RegistrationReviewItem[];
  activeIndex: number;
  reviewedCount: number;
  busy: boolean;
  /** False while a dialog is open, so P / B / arrows don't fire behind it. */
  keyboardEnabled: boolean;
  onIndexChange: (index: number) => void;
  onPersonal: (item: RegistrationReviewItem) => void;
  onBusiness: (item: RegistrationReviewItem) => void;
};

function RegistrationCarousel({
  items,
  activeIndex,
  reviewedCount,
  busy,
  keyboardEnabled,
  onIndexChange,
  onPersonal,
  onBusiness
}: Props) {
  const current = items[activeIndex];
  const canGoBack = activeIndex > 0;
  const canGoForward = activeIndex < items.length - 1;

  useEffect(() => {
    if (!keyboardEnabled) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if (isTypingTarget(event.target) || event.ctrlKey || event.metaKey || event.altKey) return;

      const key = event.key.toLowerCase();
      if (key === 'arrowleft' && canGoBack) {
        onIndexChange(activeIndex - 1);
      } else if (key === 'arrowright' && canGoForward) {
        onIndexChange(activeIndex + 1);
      } else if (key === 'p' && current && !busy) {
        onPersonal(current);
      } else if (key === 'b' && current && !busy) {
        onBusiness(current);
      } else {
        return;
      }
      event.preventDefault();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    keyboardEnabled,
    activeIndex,
    canGoBack,
    canGoForward,
    current,
    busy,
    onIndexChange,
    onPersonal,
    onBusiness
  ]);

  if (!current) return null;

  const total = items.length + reviewedCount;
  const badge = classificationLabel(current.classification);

  return (
    <Row className="g-3">
      <Col lg={8}>
        <Card>
          <Card.Header className="d-flex align-items-center justify-content-between">
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={!canGoBack}
              onClick={() => onIndexChange(activeIndex - 1)}
              title="Previous (←)">
              <FaChevronLeft />
            </Button>
            <div className="text-center">
              <div className="fw-bold" style={{ letterSpacing: '2px', fontSize: '1.25rem' }}>
                {current.plateNumber}
                {current.state ? (
                  <span className="text-muted ms-2 small">{current.state}</span>
                ) : null}
              </div>
              <div className="small text-muted">
                {activeIndex + 1} of {items.length}
              </div>
            </div>
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={!canGoForward}
              onClick={() => onIndexChange(activeIndex + 1)}
              title="Next (→)">
              <FaChevronRight />
            </Button>
          </Card.Header>
          <Card.Body>
            <Carousel
              activeIndex={activeIndex}
              onSelect={(index) => onIndexChange(index)}
              controls={false}
              indicators={false}
              interval={null}
              keyboard={false}
              touch>
              {items.map((item, index) => (
                <Carousel.Item key={item.fileId}>
                  <RegistrationPreview
                    fileId={item.fileId}
                    fileName={item.fileName}
                    contentType={item.contentType}
                    // Sign URLs for the visible slide and its neighbours only.
                    active={Math.abs(index - activeIndex) <= 1}
                  />
                </Carousel.Item>
              ))}
            </Carousel>
          </Card.Body>
          <Card.Footer>
            <ProgressBar
              now={total === 0 ? 0 : (reviewedCount / total) * 100}
              label={`${reviewedCount} reviewed`}
              visuallyHidden={reviewedCount === 0}
            />
          </Card.Footer>
        </Card>
      </Col>

      <Col lg={4}>
        <Card className="mb-3">
          <Card.Body>
            <div className="text-muted small">Client</div>
            <h5 className="mb-1">
              <Link to={`/clients/${current.clientId}`}>{current.clientName}</Link>
            </h5>
            <div className="small">{current.clientEmail || <em>No email on file</em>}</div>
            <div className="small mb-3">{current.clientPhone || <em>No phone on file</em>}</div>

            <div className="text-muted small">Currently</div>
            <div className="mb-3">
              <Badge bg={badge.bg}>{badge.label}</Badge>
              {current.classificationReviewedAt ? (
                <span className="small text-muted ms-2">
                  verified by {current.classificationReviewedByName || 'unknown'} on{' '}
                  {formatDate(current.classificationReviewedAt)}
                </span>
              ) : (
                current.classification !== PlateClassification.Unassigned && (
                  <span className="small text-muted ms-2">set by hand, not verified</span>
                )
              )}
            </div>

            <div className="text-muted small">Registration file</div>
            {current.source === RegistrationFileSource.EmailImport ? (
              <div className="small">
                <div>
                  Emailed by {current.sourceEmailFrom || 'unknown sender'}
                  {current.sourceEmailReceivedAt
                    ? ` on ${formatDate(current.sourceEmailReceivedAt)}`
                    : ''}
                </div>
                {current.sourceEmailSubject && (
                  <div className="text-muted text-truncate" title={current.sourceEmailSubject}>
                    &ldquo;{current.sourceEmailSubject}&rdquo;
                  </div>
                )}
                {current.importNotes && (
                  <div className="text-muted fst-italic mt-1">{current.importNotes}</div>
                )}
              </div>
            ) : (
              <div className="small">Uploaded {formatDate(current.uploadedAt)}</div>
            )}
          </Card.Body>
        </Card>

        <Card>
          <Card.Body>
            <div className="fw-bold mb-2">Who is this plate registered to?</div>
            <div className="d-grid gap-2">
              <Button
                size="lg"
                variant={
                  current.classification === PlateClassification.Personal ? 'info' : 'outline-info'
                }
                disabled={busy}
                onClick={() => onPersonal(current)}>
                <FaUser className="me-2" />
                Personal <span className="small opacity-75">(P)</span>
              </Button>
              <Button
                size="lg"
                variant={
                  current.classification === PlateClassification.Business
                    ? 'danger'
                    : 'outline-danger'
                }
                disabled={busy}
                onClick={() => onBusiness(current)}>
                <FaBuilding className="me-2" />
                Business / Corporation <span className="small opacity-75">(B)</span>
              </Button>
            </div>
            <div className="small text-muted mt-2">
              Business stops monitoring and tells the client we can&apos;t fight tickets on this
              plate. You&apos;ll confirm first.
            </div>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
}

export default RegistrationCarousel;
